import { Injectable } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';
import { Base64UrlService } from '@app/services/base64url.service';
import { Style, TextAlign } from '@app/core/services/core-component.service';

@Injectable({ providedIn: 'root' })
export class TerminalService {
    constructor(private readonly datatask: DataTaskService, private readonly base64: Base64UrlService) {}

    /** @datatask 2360 */
    private async request(parameters: TerminalScanRequest): Promise<TerminalScanGet> {
        const encoded: string = await this.datatask.Post(2360, parameters);
        return JSON.parse(this.base64.urlDecode(encoded));
    }
    private encode(value: string): string {
        return value ? this.base64.urlEncode(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') : '';
    }
    public init(route: string): Promise<TerminalScanGet> { return this.request({ type: 'init', route }); }
    public list(route: string, item: TerminalListItem, sessionData: string): Promise<TerminalScanGet> {
        return this.request({ type: 'list', route, value: item.item_id, sessiondata: this.encode(sessionData || '') });
    }
    public select(route: string, type: 'selectA' | 'selectB', value: string | number, sessionData: string): Promise<TerminalScanGet> {
        return this.request({ type, route, [type]: value, sessiondata: this.encode(sessionData || '') });
    }
    public scan(route: string, value: string, number: number | string, ttNumber: number | string, sessionData: string): Promise<TerminalScanGet> {
        return this.request({ type: 'scan', route, scandata: this.encode(value || ''), number: number || 0, ttNumber: ttNumber || 0, sessiondata: this.encode(sessionData || '') });
    }
    public number(route: string, type: TerminalNumberKey, number: number | string, sessionData: string): Promise<TerminalScanGet> {
        return this.request({ type, route, number: number || 0, sessiondata: this.encode(sessionData || '') });
    }
    public button(route: string, type: string, page: TerminalScanGet): Promise<TerminalScanGet> {
        // The legacy endpoint names button A "button" and ignores the inputA/inputB arguments.
        return this.request({ type: type.toUpperCase() === 'A' ? 'button' : 'button' + type.toUpperCase(), route,
            scandata: this.encode(page.scan.value || ''), number: page.number.value || 0,
            selectA: page.selectA.value || '', selectB: page.selectB.value || '', sessiondata: this.encode(page.sessionData || '') });
    }
}
export type TerminalNumberKey = 'number' | 'ttNumber' | 'ttNumber2' | 'ttNumber3';
export interface TerminalField { show: boolean; style?: string | Style; }
export interface TerminalText extends TerminalField { value: string; }
export interface TerminalNumber extends TerminalField {
    value: number | string; auto?: boolean; show1?: boolean; show10?: boolean;
    options?: { decimals?: number; autoselect?: boolean; alwaysPositive?: boolean }; placeholder?: string; label?: string; readonly?: boolean;
}
export interface TerminalSelect extends TerminalField {
    value: string | number; enabled: boolean; auto: boolean; data: Record<string, string | number>[]; dataId: string; dataName: string;
}
export interface TerminalButton extends TerminalField {
    enabled: boolean; text: { value: string; align: TextAlign }; class: string; state: string; parms: string;
}
export interface TerminalListItem { item_id: string | number; item_name?: string; [key: string]: unknown; }
export interface TerminalScanGet {
    scan: TerminalField & { value: string | null; focus: boolean; setFocus: boolean };
    inputA: TerminalText; inputB: TerminalText;
    number: TerminalNumber; ttNumber: TerminalNumber; ttNumber2: TerminalNumber; ttNumber3: TerminalNumber;
    header: { show: boolean; value: string; fontSize?: number; style?: string | Partial<CSSStyleDeclaration> };
    detailsA: TerminalText; detailsB: TerminalText; detailsC: TerminalText;
    background: { class: string; timer: number };
    list: { show: boolean; data: TerminalListItem[]; style?: { size?: string } };
    buttonA: TerminalButton; buttonB: TerminalButton; buttonC: TerminalButton; buttonD: TerminalButton; buttonE: TerminalButton; buttonF: TerminalButton;
    selectA: TerminalSelect; selectB: TerminalSelect;
    scanDelay: number; sessionData: string; isError: boolean; errorMessage: string;
}
export interface TerminalScanRequest {
    type: string; route: string; value?: string | number; sessiondata?: string; scandata?: string;
    number?: number | string; ttNumber?: number | string; selectA?: string | number; selectB?: string | number;
}
